'use client';

import { useEffect, useState } from 'react';
import { Send, Check, Clock, RefreshCw, AlertTriangle } from 'lucide-react';

interface Post {
  id: string; project_id: string; platform: string; hook?: string; caption?: string;
  topic?: string; status: string; post_url?: string; error?: string;
  scheduled_at?: number; posted_at?: number; created_at?: number; content_type?: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  approved: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  scheduled: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  published: 'bg-green-500/20 text-green-300 border-green-500/30',
  failed: 'bg-red-500/20 text-red-300 border-red-500/30',
};

export default function PublishQueuePage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [adapters, setAdapters] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState<string | null>(null);
  const [when, setWhen] = useState('');

  const load = () => {
    fetch('/api/content/posts').then(r => r.json()).then(d => setPosts(Array.isArray(d) ? d : []));
    fetch('/api/content/publish').then(r => r.json()).then(d => setAdapters(d.adapters || {}));
  };
  useEffect(load, []);

  const patch = async (id: string, body: any) => {
    setBusy(id);
    await fetch(`/api/content/posts/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    setBusy(null); load();
  };

  const publishNow = async (id: string) => {
    setBusy(id);
    await fetch('/api/content/publish', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ postId: id }) });
    setBusy(null); load();
  };

  const anyAdapter = Object.values(adapters).some(Boolean);

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Publish Queue</h1>
          <p className="text-sm text-gray-500 mt-1">Draft → approve → schedule → published. Nothing goes out without approval.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-gray-400"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {!anyAdapter && (
        <div className="glass-card rounded-xl p-4 flex items-start gap-3 border border-yellow-500/20">
          <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
          <div className="text-sm text-gray-300">
            <b className="text-yellow-300">No publishing platform connected yet.</b> The queue works (approve + schedule),
            but publishing needs credentials: add <code className="text-cyan-300">POSTIZ_URL</code> + <code className="text-cyan-300">POSTIZ_API_KEY</code> to
            <code className="text-cyan-300"> ui/.env.local</code> (Postiz covers LinkedIn/IG/X/TikTok/YouTube with one key).
          </div>
        </div>
      )}

      {posts.length === 0 && <p className="text-sm text-gray-500">No posts yet — create content in Create/Studio, it lands here for approval.</p>}

      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.id} className="glass-card rounded-xl p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}>{p.status}</span>
                  <span className="text-[10px] text-gray-500 uppercase">{p.platform}</span>
                  {p.scheduled_at && <span className="text-[10px] text-yellow-300/70 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(p.scheduled_at).toLocaleString()}</span>}
                </div>
                <p className="text-sm text-white truncate">{p.hook || p.topic || '(untitled)'}</p>
                {p.caption && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.caption}</p>}
                {p.error && <p className="text-xs text-red-400 mt-1">⚠ {p.error}</p>}
                {p.post_url && <a href={p.post_url} target="_blank" className="text-xs text-cyan-400 hover:underline">{p.post_url}</a>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {p.status === 'draft' && (
                  <button disabled={busy === p.id} onClick={() => patch(p.id, { status: 'approved' })}
                    className="text-xs px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Approve
                  </button>
                )}
                {['approved', 'scheduled'].includes(p.status) && (
                  <>
                    {scheduling === p.id ? (
                      <span className="flex items-center gap-1">
                        <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)}
                          className="text-xs bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1.5 text-gray-300" />
                        <button onClick={() => { if (when) { patch(p.id, { status: 'scheduled', scheduled_at: new Date(when).getTime() }); setScheduling(null); } }}
                          className="text-xs px-2 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">Set</button>
                      </span>
                    ) : (
                      <button onClick={() => setScheduling(p.id)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-white/[0.04] text-gray-300 border border-white/[0.08] hover:bg-white/[0.08] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Schedule
                      </button>
                    )}
                    <button disabled={busy === p.id} onClick={() => publishNow(p.id)}
                      className="text-xs px-3 py-1.5 rounded-lg bg-green-500/20 text-green-300 border border-green-500/30 hover:bg-green-500/30 flex items-center gap-1">
                      <Send className="w-3 h-3" /> Publish now
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
